import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sun, CheckCircle, Circle, ClockCounterClockwise,
  PaperPlaneTilt, ArrowRight, Flag, Sparkle,
} from "@phosphor-icons/react";
import MarkdownDocument from "@/components/MarkdownDocument";
import WeatherWidget from "@/components/WeatherWidget";
import MastheadWeather, { MastheadLocation } from "@/components/MastheadWeather";
import SunriseSunsetRail from "@/components/SunriseSunsetRail";
import useGoldenHour from "@/hooks/useGoldenHour";
import useNow from "@/hooks/useNow";
import { CLIENT_HEALTH_LABEL, ENGAGEMENT_HEALTH_LABEL } from "@/lib/enumLabels";

/**
 * Editorial hero — YadidOS morning brief.
 *
 * Frame: the panel reads like a Sunday paper — a mono/brass masthead
 * (date · weather · actions), a serif greeting with italic brass first
 * name, an opinionated AI narrative, three stat blocks, a three-card
 * priority row (the "clear these first" hero), and a coming-due list.
 * Below the fold, secondary lists (at-risk clients, flagged engagements,
 * open tasks) keep the same aesthetic but read as calmer body text.
 *
 * Stickiness: reads the same cached payload the 07:00 UTC email uses;
 * Regenerate is the only path that re-invokes the LLM.
 */

// Underline hover for all inline record links inside the brief.
const LINK_CLS =
  "hover:text-primary hover:underline underline-offset-2 " +
  "focus-visible:outline-none focus-visible:text-primary focus-visible:underline " +
  "transition-colors";

// Playfair Display is preloaded in /public/index.html. Applied inline
// so we don't need to extend tailwind config for a single component.
const SERIF = { fontFamily: "'Playfair Display', 'Merriweather', Georgia, serif" };

// Format the top-of-panel dateline, e.g. "Monday · February 24 · 5:30 AM".
// Always renders from a live timestamp — never from a cached "as of"
// value — because a dateline that lies about the current time is worse
// than not showing one.
const formatDateline = (ms) => {
  const d = new Date(ms);
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const md = d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${weekday} · ${md} · ${time}`;
};

// Time-of-day greeting driven by the partner's local clock. Uses the
// caller's clock (not brief.as_of) so opening the app in the evening
// swaps "Good morning" → "Good evening" even if the cached brief was
// generated at 07:00 UTC.
const greetingForNow = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export default function TodayPanel() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async ({ force = false } = {}) => {
    if (force) setRegenerating(true); else setLoading(true);
    try {
      const { data } = await api.get(`/briefs/my-morning/preview${force ? "?force=true" : ""}`);
      setBrief(data);
      if (force) toast.success("Morning brief regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your morning brief");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const complete = async (c) => {
    try {
      await api.post(`/notes/${c.note_id}/action-items/${c.idx}/complete`, { done: true });
      setBrief((b) => b ? ({
        ...b,
        overdue_commitments: b.overdue_commitments.filter((x) => !(x.note_id === c.note_id && x.idx === c.idx)),
        soon_commitments: b.soon_commitments.filter((x) => !(x.note_id === c.note_id && x.idx === c.idx)),
      }) : b);
      toast.success("Marked done");
    } catch (e) {
      toast.error("Could not update commitment");
    }
  };

  const sendMe = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/briefs/my-morning/send-now");
      if (data.email_id) toast.success("Sent to your inbox");
      else toast.info("Nothing to surface today — no email sent.");
    } catch (e) {
      toast.error("Send failed");
    } finally { setSending(false); }
  };

  // Priority stream is derived every render so hooks stay ordered even
  // during loading/empty states.
  const priority = useMergedPriority(brief);
  // Live clock — the dateline re-reads every minute so the top of the
  // panel always tells the truth about what time it is, not what time
  // the cached brief was generated.
  const now = useNow();
  // Golden-hour cue — greeting fades toward brass in the 30 min before
  // sunset. Hook is always called; consumers just get `active: false`
  // when we're outside the window.
  const golden = useGoldenHour();

  // ── Loading state — retains the editorial silhouette so the panel
  // doesn't jump on hydration.
  if (loading) {
    return (
      <div className="border border-border bg-card md:col-span-3 overflow-hidden" data-testid="today-panel-loading">
        <div className="px-8 py-10">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-brass flex items-center gap-1.5">
            <Sun size={12} weight="fill" /> Morning Brief
          </div>
          <div className="text-sm text-muted-foreground mt-4">Assembling your day…</div>
        </div>
      </div>
    );
  }
  if (!brief) return null;

  // ── Derive priority row (top 3 = overdue first, then soonest due) so the
  // partner sees a single opinionated "clear these first" hero.
  const overdueCount = brief.overdue_commitments?.length || 0;
  const soonCount = brief.soon_commitments?.length || 0;
  const clientRiskCount = (brief.at_risk_clients?.length || 0) + (brief.flagged_engagements?.length || 0);
  const empty = !overdueCount && !soonCount && !clientRiskCount && !(brief.open_tasks?.length);
  const firstName = (brief.partner_name || "").trim().split(/\s+/)[0] || "";

  return (
    <div className="border border-primary/30 bg-card md:col-span-3 overflow-hidden" data-testid="today-panel">
      {/* ═════════ MASTHEAD ═════════ */}
      <div className="relative px-8 pt-8 pb-7 border-b border-border">
        {/* Brass glow accent — anchors the eye to the top-right actions */}
        <div
          className="pointer-events-none absolute top-0 right-0 w-80 h-80 opacity-70"
          style={{ background: "radial-gradient(circle at top right, hsl(var(--brass) / 0.12), transparent 60%)" }}
        />
        <div className="relative flex items-start justify-between gap-6 mb-8">
          {/* Left: dateline stack (date+time on top, location+condition below). */}
          <div className="min-w-0">
            <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-brass flex items-center gap-1.5" data-testid="brief-dateline">
              <Sun size={11} weight="fill" /> {formatDateline(now)}
            </div>
            <div className="mt-1">
              <MastheadLocation />
            </div>
          </div>
          {/* Right: weather stack (temperature on top, high/low/rain below). */}
          <div className="shrink-0">
            <MastheadWeather />
          </div>
        </div>

        {/* Serif greeting. In the last 30 min before sunset the "Good X"
            portion fades toward brass — the italic name is already
            brass so it stays put; only the leading phrase drifts warm
            to nod to golden hour. `color-mix` is well-supported across
            evergreen browsers. */}
        <h1
          className="relative text-5xl md:text-6xl font-bold leading-[1.02] tracking-tight text-foreground transition-colors duration-1000"
          style={{
            ...SERIF,
            color: golden.active
              ? `color-mix(in oklab, hsl(var(--foreground)) ${Math.round((1 - golden.progress) * 100)}%, hsl(var(--brass)) ${Math.round(golden.progress * 100)}%)`
              : undefined,
            textShadow: golden.active
              ? `0 0 ${Math.round(golden.progress * 24)}px hsl(var(--brass) / ${(golden.progress * 0.35).toFixed(2)})`
              : undefined,
          }}
          data-testid="brief-greeting"
          data-golden-hour={golden.active ? "true" : "false"}
        >
          {greetingForNow()}
          {firstName ? (
            <>
              ,<br />
              <span className="text-brass italic">{firstName.endsWith(".") ? firstName : `${firstName}.`}</span>
            </>
          ) : "."}
        </h1>

        {/* AI narrative — supports markdown, styled as an opinionated lede */}
        {brief.narrative ? (
          <div className="relative mt-5 max-w-3xl text-[15px] leading-[1.65] text-muted-foreground prose-narrative">
            <MarkdownDocument>{brief.narrative}</MarkdownDocument>
          </div>
        ) : empty ? (
          <p className="relative mt-5 text-[15px] italic text-muted-foreground">
            Nothing overdue, no red flags, no open tasks. Rare. Enjoy it.
          </p>
        ) : null}

        {/* Sunrise → sunset rail paired with the brief actions. This is
            the "act on it" line: the day's arc on the left, and the two
            buttons that let you refresh or send the brief on the right.
            Buttons live down here (Layout D) so the top of the panel
            stops feeling crowded. */}
        <div className="relative mt-7 pt-6 border-t border-border/60 flex items-end justify-between gap-6">
          <div className="flex-1 min-w-0 -mt-2">
            <SunriseSunsetRail />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => load({ force: true })}
              disabled={regenerating}
              data-testid="today-refresh"
              className="text-[0.625rem] font-mono uppercase tracking-widest border border-border text-muted-foreground hover:border-brass hover:text-brass px-3 py-1.5 disabled:opacity-60 inline-flex items-center gap-1.5 transition-colors"
            ><Sparkle size={11} weight={regenerating ? "regular" : "fill"} /> {regenerating ? "Regenerating…" : "Regenerate"}</button>
            <button
              onClick={sendMe}
              disabled={sending}
              data-testid="today-send-me"
              className="text-[0.625rem] font-mono uppercase tracking-widest bg-brass text-background border border-brass px-3 py-1.5 hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-1.5 font-semibold"
            ><PaperPlaneTilt size={11} weight="bold" /> {sending ? "Sending…" : "Email me"}</button>
          </div>
        </div>

        {/* Mobile-only weather chip — on narrow screens the split
            dyad collapses to a single column so we surface weather
            again below the narrative so it isn't lost. */}
        <div className="md:hidden mt-5">
          <WeatherWidget />
        </div>

        {/* Three stat blocks — colored left rail encodes priority at a glance */}
        <div className="relative grid grid-cols-3 gap-3 md:gap-5 mt-7">
          <StatBlock tone="rose" n={overdueCount} label="Overdue · needs today" />
          <StatBlock tone="amber" n={soonCount} label="Due this week" />
          <StatBlock tone={clientRiskCount ? "amber" : "emerald"} n={clientRiskCount} label="Client risk" />
        </div>
      </div>

      {/* ═════════ BODY ═════════ */}
      {!empty && (
        <div className="px-8 py-8">
          {priority.length > 0 && (
            <>
              <SectionRule>Priority — clear these {priority.length === 1 ? "first" : "first three"}</SectionRule>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-9">
                {priority.map((c, i) => (
                  <PriorityCard key={`${c.note_id}-${c.idx}`} rank={i + 1} commitment={c} onComplete={complete} />
                ))}
              </div>
            </>
          )}

          {soonCount > 0 && (
            <div data-testid="today-soon">
              <SectionRule>Coming due this week</SectionRule>
              <ul className="mb-8">
                {brief.soon_commitments.slice(0, 8).map((c) => (
                  <CommitmentRow key={`s-${c.note_id}-${c.idx}`} commitment={c} onComplete={complete} />
                ))}
              </ul>
            </div>
          )}

          {(brief.at_risk_clients?.length || brief.flagged_engagements?.length) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {brief.at_risk_clients?.length > 0 && (
                <div data-testid="today-clients">
                  <SectionRule tone="amber">Clients needing you · {brief.at_risk_clients.length}</SectionRule>
                  <ul className="space-y-2">
                    {brief.at_risk_clients.slice(0, 5).map((c) => (
                      <li key={c.id} className="text-sm flex items-center gap-2">
                        <Flag size={11} weight="fill" className="text-amber-500 shrink-0" />
                        <Link
                          to={`/clients/${c.id}`}
                          data-testid={`brief-client-${c.id}`}
                          className={`text-foreground font-medium ${LINK_CLS}`}
                        >{c.name}</Link>
                        <span className="text-[0.625rem] font-mono uppercase tracking-widest ml-auto text-destructive">{CLIENT_HEALTH_LABEL[c.relationship_health] || c.relationship_health}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {brief.flagged_engagements?.length > 0 && (
                <div data-testid="today-engagements">
                  <SectionRule tone="amber">Flagged engagements · {brief.flagged_engagements.length}</SectionRule>
                  <ul className="space-y-2">
                    {brief.flagged_engagements.slice(0, 5).map((e) => (
                      <li key={e.id} className="text-sm flex items-center gap-2">
                        <Flag size={11} weight="fill" className={e.health === "red" ? "text-destructive shrink-0" : "text-amber-500 shrink-0"} />
                        <Link
                          to={`/engagements/${e.id}`}
                          data-testid={`brief-engagement-${e.id}`}
                          className={`text-foreground font-medium ${LINK_CLS}`}
                        >{e.name}</Link>
                        <span className={`text-[0.625rem] font-mono uppercase tracking-widest ml-auto ${e.health === "red" ? "text-destructive" : "text-amber-500"}`}>{ENGAGEMENT_HEALTH_LABEL[e.health] || e.health}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}

          {brief.open_tasks?.length > 0 && (
            <div data-testid="today-tasks">
              <SectionRule>Open tasks · {brief.open_tasks.length}</SectionRule>
              <ul className="space-y-2">
                {brief.open_tasks.slice(0, 8).map((t) => (
                  <li key={t.id} className="text-sm flex items-center gap-2">
                    <ClockCounterClockwise size={11} className="text-muted-foreground shrink-0" />
                    <Link
                      to={
                        t.engagement_id
                          ? `/engagements/${t.engagement_id}?tab=tasks&openTask=${t.id}`
                          : `/tasks?open=${t.id}`
                      }
                      data-testid={`brief-task-${t.id}`}
                      className={`text-foreground ${LINK_CLS}`}
                    >{t.title}</Link>
                    {t.due_date && (
                      <span className="text-[0.625rem] font-mono uppercase tracking-widest ml-auto text-muted-foreground">due {t.due_date}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

/**
 * Merge overdue + soon into a single priority stream, take top 3.
 * Overdue always wins; within each bucket we preserve the server's
 * order (which is already sorted by urgency).
 */
function useMergedPriority(brief) {
  return useMemo(() => {
    if (!brief) return [];
    const overdue = (brief.overdue_commitments || []).map((c) => ({ ...c, _kind: "overdue" }));
    const soon = (brief.soon_commitments || []).map((c) => ({ ...c, _kind: "soon" }));
    return [...overdue, ...soon].slice(0, 3);
  }, [brief]);
}

function StatBlock({ tone, n, label }) {
  const railClass =
    tone === "rose" ? "border-l-destructive" :
    tone === "amber" ? "border-l-amber-500" :
    tone === "emerald" ? "border-l-emerald-500" :
    "border-l-border";
  const nClass =
    tone === "rose" ? "text-destructive" :
    tone === "amber" ? "text-amber-500" :
    tone === "emerald" ? "text-emerald-500" :
    "text-foreground";
  return (
    <div className={`bg-background/50 border-l-[3px] ${railClass} px-4 py-3 md:px-5 md:py-4`}>
      <div className={`text-3xl md:text-4xl font-bold leading-none ${nClass}`} style={SERIF}>{n}</div>
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-2">{label}</div>
    </div>
  );
}

function SectionRule({ children, tone = "muted" }) {
  const color =
    tone === "amber" ? "text-amber-500" :
    tone === "brass" ? "text-brass" :
    "text-muted-foreground";
  return (
    <h4 className={`text-[0.625rem] font-mono uppercase tracking-[0.2em] ${color} mb-4 flex items-center gap-3`}>
      <span>{children}</span>
      <span className="flex-1 h-px bg-border" />
    </h4>
  );
}

function PriorityCard({ rank, commitment, onComplete }) {
  const isOverdue = commitment._kind === "overdue";
  const target = commitmentHref(commitment);
  return (
    <div
      data-testid={`brief-priority-${commitment.note_id}-${commitment.idx}`}
      className={`relative p-5 border ${isOverdue ? "border-destructive/40" : "border-border"} bg-background/50 group hover:border-brass transition-colors`}
      style={isOverdue ? { backgroundImage: "linear-gradient(135deg, hsl(var(--destructive) / 0.06), transparent 60%)" } : undefined}
    >
      <span
        className="absolute top-3 right-4 text-3xl text-muted-foreground/30 font-bold"
        style={SERIF}
      >{String(rank).padStart(2, "0")}</span>
      <div className={`text-[0.625rem] font-mono uppercase tracking-widest mb-2 ${isOverdue ? "text-destructive" : "text-amber-500"}`}>
        {isOverdue ? `Overdue${commitment.due ? ` · ${commitment.due}` : ""}` : (commitment.due ? `Due ${commitment.due}` : "Due soon")}
      </div>
      <Link
        to={target}
        data-testid={`commitment-title-${commitment.note_id}-${commitment.idx}`}
        className={`block text-[16px] leading-snug font-semibold pr-10 mb-2 text-foreground hover:text-primary transition-colors`}
        style={SERIF}
      >{commitment.task}</Link>
      <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4 truncate">
        {[commitment.client_name, commitment.engagement_name].filter(Boolean).join(" · ") || "—"}
      </div>
      <div className="flex items-center gap-2">
        <Link
          to={target}
          className="text-[0.625rem] font-mono uppercase tracking-widest border border-brass text-brass px-2.5 py-1 hover:bg-brass hover:text-background transition-colors inline-flex items-center gap-1"
        >Open <ArrowRight size={10} weight="bold" /></Link>
        <button
          onClick={() => onComplete(commitment)}
          data-testid={`commitment-done-${commitment.note_id}-${commitment.idx}`}
          className="text-[0.625rem] font-mono uppercase tracking-widest border border-border text-muted-foreground px-2.5 py-1 hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1"
          title="Mark done"
        ><CheckCircle size={10} weight="fill" /> Done</button>
      </div>
    </div>
  );
}

/** A single row in the "Coming due" list. Small check-affordance +
 * title + subtitle + right-aligned due indicator. */
function CommitmentRow({ commitment: c, onComplete }) {
  return (
    <li className="group flex items-start gap-3 py-3 border-b border-border/60 hover:bg-brass/[0.03] transition-colors">
      <button
        onClick={() => onComplete(c)}
        data-testid={`commitment-done-${c.note_id}-${c.idx}`}
        className="text-muted-foreground hover:text-primary shrink-0 mt-1"
        title="Mark done"
      >
        <Circle size={14} className="group-hover:hidden" />
        <CheckCircle size={14} weight="fill" className="hidden group-hover:inline text-primary" />
      </button>
      <div className="flex-1 min-w-0">
        <Link
          to={commitmentHref(c)}
          data-testid={`commitment-title-${c.note_id}-${c.idx}`}
          className={`text-sm text-foreground block ${LINK_CLS}`}
        >{c.task}</Link>
        <CommitmentSubtitle commitment={c} />
      </div>
      {c.due && (
        <div className={`text-[0.625rem] font-mono uppercase tracking-widest shrink-0 pt-1 ${
          c._kind === "overdue" ? "text-destructive" : "text-muted-foreground"
        }`}>
          {c.due}
        </div>
      )}
    </li>
  );
}

/** Deep-link a commitment to the specific action-item inside its
 * source note. Mirrors the previous CommitmentLink logic. */
function commitmentHref(c) {
  const params = [];
  if (c.note_id) params.push(`openNote=${c.note_id}`);
  if (c.note_id && Number.isInteger(c.idx)) params.push(`openCommitment=${c.idx}`);
  const q = params.length ? `&${params.join("&")}` : "";
  if (c.engagement_id) return `/engagements/${c.engagement_id}?tab=notes${q}`;
  if (c.client_id) return `/clients/${c.client_id}?tab=notes${q}`;
  return "/notes";
}

function CommitmentSubtitle({ commitment: c }) {
  const parts = [];
  if (c.client_id && c.client_name) {
    parts.push(
      <Link key="client" to={`/clients/${c.client_id}`} data-testid={`commitment-client-${c.note_id}-${c.idx}`} className={LINK_CLS}>{c.client_name}</Link>
    );
  } else if (c.client_name) {
    parts.push(<span key="client">{c.client_name}</span>);
  }
  if (c.engagement_id && c.engagement_name) {
    parts.push(
      <Link key="eng" to={`/engagements/${c.engagement_id}`} data-testid={`commitment-engagement-${c.note_id}-${c.idx}`} className={LINK_CLS}>{c.engagement_name}</Link>
    );
  } else if (c.engagement_name) {
    parts.push(<span key="eng">{c.engagement_name}</span>);
  }
  if (!parts.length) parts.push(<span key="dash">—</span>);
  return (
    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground truncate">
      {parts.map((el, i) => (<span key={i}>{i > 0 && " · "}{el}</span>))}
    </div>
  );
}
